import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NebulaNavigationService } from 'nebula-ui-kit';
import {
  VaultMenuAccessService,
  VaultSidebarMenuService,
} from '@nebula/shared-vault/data-access';
import { SidebarMenuItem } from '@nebula-layout/layout';
import { VAULT_MAX_OPEN_TABS } from '../../const/src/lib/layout.constants';
import { isVaultRoute } from '../utils/nebula-route-validity';
import { applyVaultMenuLabels } from '../utils/vault-menu-labels';
import { ensurePreservationMessagesSidebarItem } from '../utils/preservation-messages-menu.util';
import { filterVaultSidebarMenuItems } from '../utils/vault-sidebar-hidden-menu.util';
import {
  countVaultOpenTabs,
  findOpenVaultTab,
  normalizeVaultMenuRoute,
} from '../utils/vault-open-tabs.util';

export interface VaultMenuSearchOption {
  label: string;
  route: string;
  icon: string;
  group: string;
  relevance?: number;
}

export type VaultMenuSearchOpenResult = 'opened' | 'activated' | 'limit' | 'denied';

@Injectable({ providedIn: 'root' })
export class VaultGlobalMenuSearchService {
  private readonly vaultSidebarMenu = inject(VaultSidebarMenuService);
  private readonly menuAccess = inject(VaultMenuAccessService);
  private readonly navService = inject(NebulaNavigationService);
  private readonly router = inject(Router);

  private readonly options = signal<VaultMenuSearchOption[]>([]);
  private loaded = false;
  private loading = false;

  loadOptions(): void {
    if (this.loaded || this.loading) return;
    this.loading = true;
    this.vaultSidebarMenu.getVaultSidebarMenu().subscribe({
      next: (tree) => {
        const items = filterVaultSidebarMenuItems(
          ensurePreservationMessagesSidebarItem(applyVaultMenuLabels(tree ?? [])),
        );
        this.options.set(this.flatten(items));
        this.loaded = true;
        this.loading = false;
      },
      error: () => {
        const fallback = filterVaultSidebarMenuItems(
          ensurePreservationMessagesSidebarItem(
            applyVaultMenuLabels(this.vaultSidebarMenu.getCanonicalFallbackMenu()),
          ),
        );
        this.options.set(this.flatten(fallback));
        this.loaded = true;
        this.loading = false;
      },
    });
  }

  invalidate(): void {
    this.loaded = false;
    this.loading = false;
    this.options.set([]);
  }

  search(query: string): VaultMenuSearchOption[] {
    this.loadOptions();
    const q = this.normalize(query);
    if (q.length < 2) return [];

    return this.options()
      .map((o) => {
        const route = normalizeVaultMenuRoute(o.route);
        return { ...o, route, relevance: this.relevance(o, q) };
      })
      .filter((o) => (o.relevance ?? 0) > 0)
      .filter((o) => isVaultRoute(o.route) && this.menuAccess.isRouteAllowed(o.route))
      .sort((a, b) => (b.relevance ?? 0) - (a.relevance ?? 0))
      .slice(0, 15);
  }

  openOption(opt: VaultMenuSearchOption): VaultMenuSearchOpenResult {
    const route = normalizeVaultMenuRoute(opt.route);
    if (!route || !isVaultRoute(route)) return 'denied';
    if (!this.menuAccess.isRouteAllowed(route)) return 'denied';

    const existing = findOpenVaultTab(this.navService.tabs(), route);
    if (existing?.id != null) {
      this.navService.selectTab(existing.id);
      void this.router.navigateByUrl(route);
      return 'activated';
    }

    if (countVaultOpenTabs(this.navService.tabs()) >= VAULT_MAX_OPEN_TABS) {
      return 'limit';
    }

    this.navService.addTabFromMenu({
      label: opt.label,
      icon: opt.icon,
      route,
    });
    void this.router.navigateByUrl(route);
    return 'opened';
  }

  private flatten(tree: SidebarMenuItem[], parentGroup = ''): VaultMenuSearchOption[] {
    const out: VaultMenuSearchOption[] = [];
    for (const item of tree || []) {
      const children = item.children ?? [];
      if (children.length > 0) {
        out.push(...this.flatten(children, item.label || parentGroup));
        continue;
      }
      if (item.route) {
        out.push({
          label: item.label,
          route: item.route,
          icon: item.icon || 'File',
          group: parentGroup,
        });
      }
    }
    return out;
  }

  private relevance(o: VaultMenuSearchOption, q: string): number {
    const label = this.normalize(o.label);
    const group = this.normalize(o.group);
    let score = 0;
    if (label === q) score += 100;
    if (label.startsWith(q)) score += 50;
    if (label.includes(q)) score += 30;
    if (group.includes(q)) score += 20;
    return score;
  }

  private normalize(str: string): string {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
}
